// Objectif hebdomadaire (TW #17573162) : une tâche à faire N fois dans la
// semaine, sans jour imposé. Helpers purs partagés entre le service de
// complétion quotidienne et l'UI (compteur "x/N cette semaine").

import { addDays } from '@/services/date.service'

export const WEEKLY_TARGET_MIN = 1
export const WEEKLY_TARGET_MAX = 7
export const WEEKLY_TARGET_DEFAULT = 3

/** Nombre d'occurrences hebdo d'une tâche, 0 si la tâche n'est pas en mode objectif hebdo.
 *  Paramètre volontairement `unknown` : weekly_target_count n'est pas (encore) dans les
 *  types DB générés, comme allowance_bonus_amount. */
export function weeklyTargetOf(task: unknown): number {
    const n = Number((task as { weekly_target_count?: number | null } | null | undefined)?.weekly_target_count ?? 0)
    return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Fenêtre de semaine locale contenant `date` ('YYYY-MM-DD'), bornes incluses.
 * `weekStartIndex` suit localeService.getWeekStartIndex() (0 = dimanche, 1 = lundi).
 * L'ancre à midi UTC fixe le jour de semaine du jour CALENDAIRE, indépendamment
 * du fuseau de l'appareil (même approche que addDays).
 */
export function weekWindowFor(date: string, weekStartIndex: number): { start: string; end: string } {
    const jsDay = new Date(`${date}T12:00:00Z`).getUTCDay()
    const offset = (jsDay - weekStartIndex + 7) % 7
    const start = addDays(date, -offset)
    return { start, end: addDays(start, 6) }
}

export interface WeeklyCountRow {
    date: string
    tasks_data?: Array<{ id?: string; task_id?: string; status?: string }> | null
}

/**
 * Compte, par tâche hebdo, les complétions sur les lignes daily_completions
 * d'une semaine. `excludeDate` retire un jour du compte : le service l'utilise
 * pour évaluer la disponibilité d'un jour D sans que la complétion de D
 * lui-même ne masque la tâche (une entrée complétée le jour D doit rester).
 */
export function countWeeklyDone(
    rows: WeeklyCountRow[],
    weeklyTaskIds: ReadonlySet<string>,
    excludeDate?: string
): Map<string, number> {
    const out = new Map<string, number>()
    for (const row of rows) {
        if (excludeDate && row.date === excludeDate) continue
        for (const td of row.tasks_data ?? []) {
            const id = td.task_id || td.id
            if (!id || !weeklyTaskIds.has(id) || td.status !== 'completed') continue
            out.set(id, (out.get(id) ?? 0) + 1)
        }
    }
    return out
}
